import { useCallback, useEffect, useRef, useState } from 'react';

export const useFilterOptionKeyboardNav = ({
  open,
  optionCount,
  onSelect,
  onClose,
}: {
  open: boolean;
  optionCount: number;
  onSelect: (index: number) => void;
  onClose: () => void;
}) => {
  const [activeIndex, setActiveIndex] = useState(-1);
  const activeIndexRef = useRef(-1);

  const setActive = useCallback((next: number) => {
    activeIndexRef.current = next;
    setActiveIndex(next);
  }, []);

  // Reset the highlight when the dropdown closes or the option set changes
  // (e.g. the building filter text narrows the list).
  useEffect(() => {
    setActive(-1);
  }, [open, optionCount, setActive]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Close only this dropdown — stop the event before it reaches the search
        // modal's Escape handler, which would otherwise close the whole overlay.
        event.preventDefault();
        event.stopPropagation();
        onClose();
        return;
      }

      if (optionCount === 0) {
        return;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActive(Math.min(activeIndexRef.current + 1, optionCount - 1));
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActive(activeIndexRef.current <= 0 ? 0 : activeIndexRef.current - 1);
      } else if (event.key === 'Enter') {
        if (activeIndexRef.current >= 0) {
          event.preventDefault();
          onSelect(activeIndexRef.current);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [open, optionCount, onSelect, onClose, setActive]);

  return { activeIndex };
};
